/**
 * The render entry point. This is the function that exists once and runs in three places.
 *
 * Order of operations, and why:
 *   1. optional flatten — App Store sets must be opaque, so an opaque surface is the
 *      default rather than a post-processing step that can be forgotten;
 *   2. background;
 *   3. layers in array order, first at the bottom.
 *
 * Layer pixel bounds are returned alongside the pixels so the template gate (collision
 * checking) and the editor's selection outlines can use the same numbers the renderer used,
 * instead of re-deriving them and drifting.
 */
import type { DeviceFrame } from '@keymaker/frames';
import type { CanvasSize, DeviceLayer, Layer, SceneDoc } from '@keymaker/schema';
import type { RenderCanvas, RenderEnvironment, RenderImage, RenderOptions } from './env';
import { RenderError } from './env';
import { quadBounds, resolveBox, resolveDeviceRect, type Rect } from './fit';
import { hidesWhatIsUnderneath } from './color';
import { projectedQuad } from './perspective';
import {
  drawBackground,
  drawDeviceLayer,
  drawElementLayer,
  drawImageLayer,
  drawShapeLayer,
  drawTextLayer,
  type DrawContext,
} from './layers';
import { layoutText } from './text';

export interface LayerBounds {
  id: string;
  type: Layer['type'];
  rect: Rect;
  /** True when this layer's bounds could collide with text (i.e. it is not text itself). */
  occludes: boolean;
  /**
   * Paint order, first at the bottom. Load-bearing for the collision check: a layer drawn
   * *before* a caption sits underneath it and cannot cover it, so only later layers are
   * candidates. Without this, a caption on top of a panel reads as a covered caption.
   */
  order: number;
}

export interface RenderResult {
  scene: SceneDoc;
  canvas: RenderCanvas;
  width: number;
  height: number;
  bounds: LayerBounds[];
  warnings: string[];
}

export interface RenderSceneOptions extends RenderOptions {
  /** Device frame library to resolve `device` layers against. */
  frames: readonly DeviceFrame[];
  /**
   * Fill the surface with an opaque colour first. Default true, because every App Store
   * screenshot must be opaque and a transparent export is only ever wanted for a preview.
   */
  opaque?: boolean;
  /** Colour used when `opaque` is on. */
  flatten?: string;
  /**
   * Build Tier 3 bodies at half resolution, for the duration of a drag.
   *
   * Nobody can see half resolution in something that moves with the pointer, and the whole cost of a
   * 3D body is in building and rendering it at full size. The interaction asks for a draft while a
   * control is live and the full render follows as soon as it is released.
   */
  draft?: boolean;
  /**
   * Draw into this surface instead of a fresh one.
   *
   * A canvas at export size is fifteen megabytes. The editor asked for a new one on every frame of a
   * drag and then copied the result into its own, so a four-second drag allocated and copied three and
   * a half gigabytes — and the collection pause for that arrived as a single 450 ms frame in the middle
   * of it, which is one frame over the hundred-millisecond budget while the rest sat at seventeen.
   *
   * The surface is cleared before anything is drawn, so handing back the same one each time is safe for
   * a caller that is done with the previous picture. A caller that still needs an earlier result — the
   * tests and the parity run hold several — should not pass one.
   */
  into?: RenderCanvas;
}

/**
 * Pixel bounds of every visible layer, with no rendering at all.
 *
 * Pure geometry: every branch below resolves normalised units to pixels and nothing touches a
 * canvas. That is why it is a separate export rather than being computed inside the draw loop.
 * Three callers need these numbers and must agree on them exactly:
 *
 *   - the collision check, which must use the geometry that was actually drawn rather than
 *     re-deriving it and drifting;
 *   - the editor's selection outlines;
 *   - template validation, which can then check a template for caption/device collisions and
 *     off-canvas text without rendering it.
 */
export function layerBounds(scene: SceneDoc, frames: readonly DeviceFrame[]): LayerBounds[] {
  const bounds: LayerBounds[] = [];

  for (const [order, layer] of scene.layers.entries()) {
    if (!layer.visible) continue;

    switch (layer.type) {
      case 'device': {
        const frame = frames.find((f) => f.id === layer.frame);
        const rect = frame
          ? resolveDeviceRect(layer, scene.canvas, frame.canvas)
          : { x: 0, y: 0, width: 0, height: 0 };
        /**
         * The Tier 2 warp widens the footprint, and it applies **only** to a flat device.
         *
         * This has to mirror `drawDeviceLayer`, which skips the warp for `kind: "3d"`: a 3D body has
         * its rotation baked into a render that is clipped to the frame canvas, so its footprint is the
         * rect and can never be more. Warping the bounds anyway made a turned device claim a footprint
         * larger than itself — and the collision check then refused the whole set with "the caption is
         * covered by the device over 57.6% of its box" for a caption nowhere near it, which made every
         * 3D panel impossible to export.
         */
        const tilted =
          layer.kind !== '3d' &&
          layer.perspective !== undefined &&
          (layer.perspective.tiltX !== 0 || layer.perspective.tiltY !== 0);
        bounds.push({
          id: layer.id,
          type: layer.type,
          rect: tilted
            ? quadBounds(
                projectedQuad(rect, {
                  tiltX: layer.perspective!.tiltX,
                  tiltY: layer.perspective!.tiltY,
                  perspective: layer.perspective!.perspective,
                }),
              )
            : rect,
          occludes: true,
          order,
        });
        break;
      }
      case 'text':
        bounds.push({
          id: layer.id,
          type: layer.type,
          rect: resolveBox(layer.box, scene.canvas),
          occludes: false,
          order,
        });
        break;
      case 'image':
        bounds.push({
          id: layer.id,
          type: layer.type,
          rect: resolveBox(layer.box, scene.canvas),
          occludes: true,
          order,
        });
        break;
      case 'element':
        bounds.push({
          id: layer.id,
          type: layer.type,
          rect: resolveBox(layer.box, scene.canvas),
          // Never an occluder. A laurel is mostly empty space inside its box, and the collision check
          // firing because a sticker's rectangle overlaps a caption would make every decorated layout
          // report a problem that is not one.
          occludes: false,
          order,
        });
        break;
      case 'shape':
        bounds.push({
          id: layer.id,
          type: layer.type,
          rect: resolveBox(layer.box, scene.canvas),
          // A shape occludes only if painting it would actually hide what is under it, and a shape
          // with no fill is a divider. Translucent panels and scrims are a normal part of these
          // designs, and treating a `#FFFFFF14` tint as an occluder makes the collision check fire
          // on every layout that uses one.
          occludes: layer.fill !== undefined && hidesWhatIsUnderneath(layer.fill),
          order,
        });
        break;
    }
  }

  return bounds;
}

export function renderScene(
  scene: SceneDoc,
  env: RenderEnvironment,
  options: RenderSceneOptions,
): RenderResult {
  const opaque = options.opaque ?? true;
  const flatten = options.flatten ?? '#000000';
  const canvas = options.into ?? env.createCanvas(scene.canvas.width, scene.canvas.height);
  const ctx = canvas.ctx;
  const warnings: string[] = [];

  const drawContext: DrawContext = {
    env,
    canvas: scene.canvas,
    options,
    frames: options.frames,
  };

  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, scene.canvas.width, scene.canvas.height);
  if (opaque) {
    ctx.fillStyle = flatten;
    ctx.fillRect(0, 0, scene.canvas.width, scene.canvas.height);
    if (scene.background.type === 'none') {
      warnings.push(
        'Scene background is "none" but the export is opaque; the canvas shows the flatten colour. ' +
          'Give the scene a background, or render with opaque: false for a preview.',
      );
    }
  }

  drawBackground(ctx, scene.background, drawContext);

  for (const layer of scene.layers) {
    if (!layer.visible) continue;

    switch (layer.type) {
      case 'device': {
        const render = drawDeviceLayer(ctx, layer, drawContext);
        if (render.aspectMismatch !== null) {
          warnings.push(
            `Device layer "${layer.id}": capture aspect differs from the display by ` +
              `${(render.aspectMismatch * 100).toFixed(1)}%; it was fitted with "${layer.screenFit}".`,
          );
        }
        break;
      }
      case 'text': {
        drawTextLayer(ctx, layer, drawContext);
        const layout = layoutText(ctx, layer, scene.canvas);
        if (layout.overflow) {
          warnings.push(
            `Text layer "${layer.id}" does not fit its box even at the minimum shrink size ` +
              `(${layout.fontSize.toFixed(1)}px, requested ${layout.requestedFontSize.toFixed(1)}px). ` +
              'Rewrap, enlarge the box, or reduce the copy.',
          );
        }
        break;
      }
      case 'image':
        drawImageLayer(ctx, layer, drawContext);
        break;
      case 'shape':
        drawShapeLayer(ctx, layer, drawContext);
        break;
      case 'element': {
        const warning = drawElementLayer(ctx, layer, drawContext);
        if (warning) warnings.push(warning);
        break;
      }
    }
  }

  return {
    scene,
    canvas,
    width: scene.canvas.width,
    height: scene.canvas.height,
    bounds: layerBounds(scene, options.frames),
    warnings,
  };
}

/** Render several scenes, reusing one environment and asset map. */
export function renderScenes(
  scenes: readonly SceneDoc[],
  env: RenderEnvironment,
  options: RenderSceneOptions,
): RenderResult[] {
  return scenes.map((scene) => renderScene(scene, env, options));
}

/**
 * Resolve every Tier 3 device body in a scene, ready to pass to `renderScene` as `devices`.
 *
 * The one asynchronous step in the renderer, kept explicit so the compositing path stays
 * synchronous: a 500-panel batch would otherwise be 500 promises deep, and the promise would be
 * paid by every project including the ones that never touch Tier 3.
 *
 * Returns an empty map and does nothing when a scene has no `kind: "3d"` layers, so calling it
 * unconditionally is free.
 */
export async function prepare3dDevices(
  scene: SceneDoc,
  env: RenderEnvironment,
  options: RenderSceneOptions,
): Promise<Map<string, RenderImage>> {
  const wanted = scene.layers.filter(
    (layer): layer is DeviceLayer => layer.type === 'device' && layer.kind === '3d',
  );
  const prepared = new Map<string, RenderImage>();
  if (wanted.length === 0) return prepared;

  if (!env.render3d) {
    throw new RenderError(
      `Scene "${scene.family ?? 'unknown'}" uses ${wanted.length} device layer(s) of kind "3d", ` +
        'but this environment provides no render3d. Tier 3 needs WebGL; use the browser adapter, ' +
        'or set kind to "frame".',
      '3d-unavailable',
    );
  }

  for (const layer of wanted) {
    const frame = options.frames.find((f) => f.id === layer.frame);
    if (!frame) {
      throw new RenderError(
        `Device layer "${layer.id}" names unknown frame "${layer.frame}".`,
        'unknown-frame',
      );
    }

    const screen = options.assets?.get(layer.screen);
    if (!screen) {
      if (options.onMissingAsset) {
        options.onMissingAsset(layer.screen, `3d device layer "${layer.id}"`);
        continue;
      }
      throw new RenderError(
        `Missing asset "${layer.screen}" required by 3d device layer "${layer.id}".`,
        'missing-asset',
      );
    }

    // `perspective` becomes real 3D rotation for a 3D body, which is the point of the tier: the
    // same field that fakes depth on a flat bezel produces actual depth here.
    const rotation = layer.perspective
      ? { x: layer.perspective.tiltX, y: layer.perspective.tiltY, z: layer.transform.rotation }
      : { z: layer.transform.rotation };

    prepared.set(
      layer.id,
      await env.render3d({
        // Named, so an adapter can reuse one output buffer per device instead of allocating several
        // megabytes every frame of a drag.
        key: layer.id,
        frame,
        screen,
        // Rendering at the frame canvas size is what keeps placement identical to Tier 1:
        // `resolveDeviceRect` and the layer bounds need no special case for 3D. A draft is the same
        // picture at half the resolution, which the compositor scales into the same rect.
        width: Math.max(1, Math.round(frame.canvas.width * (options.draft ? 0.5 : 1))),
        height: Math.max(1, Math.round(frame.canvas.height * (options.draft ? 0.5 : 1))),
        styling: layer.body,
        rotation,
      }),
    );
  }

  return prepared;
}

/**
 * Convenience for the common case: prepare Tier 3 bodies, then render.
 *
 * Equivalent to calling `prepare3dDevices` and `renderScene` in sequence; scenes with no 3D
 * layers take the fast path and never await anything meaningful.
 */
export async function renderSceneComplete(
  scene: SceneDoc,
  env: RenderEnvironment,
  options: RenderSceneOptions,
): Promise<RenderResult> {
  const devices = await prepare3dDevices(scene, env, options);
  return renderScene(scene, env, devices.size > 0 ? { ...options, devices } : options);
}

export function canvasSizeOf(scene: SceneDoc): CanvasSize {
  return { width: scene.canvas.width, height: scene.canvas.height };
}
